import axiosInstance from "utils/axios-request";
import {baseUrl, gatewayUrl, xApiKey} from "config";
import {IProfile} from "profile/interface";
import {IUTMParams} from "StateInterface";
import {getIdToken} from "service";
import {notifyDownload} from "utils/android";
import {isWebview} from "utils";
import {ENV} from "Dashboard/utils";

export enum Type {
    URL = "URL",
    BS64 = "BS64",
}

export enum DocType {
    KFS = "KFS",
}

interface ILogData {
    type: "info" | "error" | "warn";
    options: {
      [key: string]: string | number | object | boolean;
    };
}

export const markConsent = (opportunityId: string, consent: boolean, comment: string) =>
    axiosInstance.request({
        baseURL: gatewayUrl,
        url: "/hagen/consent",
        method: "post",
        data: {
            opportunityId,
            consent,
            comment,
        },
    });

export const getPostApprovalFormFields = (opportunityId: string) =>
    axiosInstance.request({
        baseURL: gatewayUrl,
        url: `/hagen/pa-form/?opportunityId=${opportunityId}`,
        method: "get",
    });

export const updatePaField = (opportunityId: string, paComplete: boolean) =>
    axiosInstance.request({
        baseURL: gatewayUrl,
        url: "/hagen/pa-form",
        method: "POST",
        data: {
          opportunityId,
          paComplete,
        },
    });

export const saveOpportunityProfile = (opportunityId: string, profileData: IProfile) =>
  axiosInstance.request({
      baseURL: gatewayUrl,
      url: "/hagen/pa-form",
      method: "post",
      data: {
          opportunityId,
          ...profileData,
      },
  });

export const getProcessModes = (productCode: String, lenderId: number, opportunityId: string) => {
    return axiosInstance.request({
        baseURL: gatewayUrl,
        url: `/hagen/document-collection-modes`,
        method: "get",
        params: {productCode, lenderId, opportunityId},
    });
};

export const getOpportunity = async (id: string) => {
    const response = await axiosInstance.request({
        baseURL: gatewayUrl,
        url: `/loan-opportunity/opportunity`,
        method: "get",
        params: {id},
    });
    return response.data;
};

export const updateAlternateNumber = async (referenceMobile: string, referenceName: string, userId: number) => {
    const response = await axiosInstance.request({
        baseURL: gatewayUrl,
        url: `/paandu/profile?userId=${userId}`,
        method: "post",
        data: {
            profileData: {
                references: [{
                    referenceMobile,
                    referenceName,
                }],
            },
        },
    });
    return response;
};

export const logError = async (
  {type, options}: ILogData,
) => {
  await axiosInstance.request({
    baseURL: gatewayUrl,
    url: "events/fe-logs",
    headers: {
      "X-API-KEY": xApiKey,
    },
    method: "POST",
    data: {
      type,
      options,
    },
  });
};

export const getPreSignedUrlDoc = async (userId: number, docType: string, docName: string) => {
    const response = await axiosInstance.request({
      baseURL:gatewayUrl,
      url: "document-gateway/fetch-doc-pre-signed-url",
      method: "get",
      params: {
        userId,
        docType,
        docName,
      },
    });
    return response.data?.image;
  };

const downloadPdfFromBase64 = (pdf: string, fileName: string) => {
    const linkSource = `data:application/pdf;base64,${pdf}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = `${fileName}.pdf`;
    downloadLink.click();
};

export const downloadPdf = (type: Type, value: string, filename: string) => {
    if (type === Type.URL) {
      fetch(value).then((response) => {
        return response.blob().then((blob) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.addEventListener("load", () => {
            if (typeof reader.result === "string") {
              const base64Data = reader.result.replace(/^data:.+;base64,/, "");
              downloadPdfFromBase64(base64Data, filename);
            }
          });
        });
      });
    } else if (type === Type.BS64) {
      if (isWebview() && window.location.href.includes("android")) {
        notifyDownload(filename, value, null, "application/pdf");
      } else {
        downloadPdfFromBase64(value, filename);
      }
    }
};

export const getBase64FromUrl = async (url) => {
    const data = await fetch(url);
    const blob = await data.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        resolve(base64data);
      };
    });
}
